//! Featherify (previously AjaxifyJS) by James Powell - Version 1.3.2
// Required IE11 polyfills: Promise, regenerator-runtime, URL constructor

/*
Events:
 1. beforeUnmountAnimation
 2. afterUnmountAnimation
 3. loadingData: The scripts, styles and document are being loaded.
 4. progress: Progress on download has been made.
 5. loadedData: The scripts, styles and document have been loaded.
 6. beforeUnmount X: Before X has been unmounted, where X is the name of a script file.
 7. beforeUnmount: The old document is about to be unmounted.
 8. unmounted: The old document has been unmounted.
 9. mounted: The new document has been mounted.
 10. beforeMountAnimation
 11. afterMountAnimation
*/

/*
Methods:
 - animate(handler: (animatedCallback) => void) => void
 - on(eventName: string, handler: (eventData) => void, priority: number = 0, callLifespan: number = Infinity) => handler
 - onUntil(eventName: string, handler: (eventData) => void, untilEventName: string, priority: number = 0) => handler
 - once(eventName: string, handler: (eventData) => void, priority: number = 0) => handler
 - off(eventName: string, handler: (eventData) => void) => void
 - go(location: (URL | string), pushState: boolean = true, triggerElement: HTMLElement) => void
 - listen() => Featherify
*/

/*
Animation Classes:
 - featherify-entering
 - featherify-mounted
 - featherify-leaving
*/

export default class Featherify {
  constructor(options) {
    this.goQueue = [];
    this.loading = false;
    this.eventListeners = [];
    this.animationsAwaiting = 0;
    this.currentLocation = new URL(window.location.href);
    this.options = Object.assign({
      contentContainerSelector: 'body',
      bodyTransitionClasses: true
    }, options);

    this.eventData = {
      triggerElement: null,
      newDocument: null,
      target: null,
      recipientLocation: null
    };

    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    window.addEventListener('popstate', (e) => {
      this.go(window.location, false);

      e.preventDefault();
    });
  }

  animate(handler) {
    this.animationsAwaiting++;

    const animatedHandler = () => {
      if (!--this.animationsAwaiting && this.animatedHandler) {
        this.animatedHandler();
      }
    };

    if (typeof handler === 'number') {
      setTimeout(animatedHandler, handler);

      return;
    }

    handler(animatedHandler);
  }

  on(eventName, handler, priority = 0, callLifespan = Infinity) {
    this.eventListeners.push({
      eventName,
      handler,
      callLifespan,
      priority
    });

    return handler;
  }

  once(eventName, handler, priority) {
    return this.on(eventName, handler, priority, 1);
  }

  onUntil(eventName, handler, untilEventName, priority = 0) {
    this.eventListeners.push({
      eventName,
      handler,
      untilEventName,
      priority
    });

    return handler;
  }

  off(eventName, handler) {
    for (const listenerObj of this.eventListeners) {
      if (listenerObj.eventName === eventName && listenerObj.handler === handler) {
        this.eventListeners.splice(this.eventListeners.indexOf(listenerObj), 1);
      }
    }
  }

  _triggerEvent(eventName, ...params) {
    const triggeredListeners = [];
    this.eventListeners = this.eventListeners.filter((listenerObj) => {
      if (listenerObj.untilEventName === eventName) {
        return false;
      }

      if (listenerObj.eventName !== eventName) {
        return true;
      }

      triggeredListeners.push(listenerObj);

      return !(typeof listenerObj.callLifespan !== 'undefined' && --listenerObj.callLifespan === 0);
    });

    for (const listenerObj of triggeredListeners.sort((a, b) => {
      return a.priority - b.priority;
    })) {
      listenerObj.handler(this.eventData, ...params);
    }
  }

  _cloneAttributes(donorElement, recipientElement) {
    for (const attr of recipientElement.attributes) {
      recipientElement.removeAttribute(attr.name);
    }

    for (const attr of donorElement.attributes) {
      recipientElement.setAttribute(attr.name, attr.value);
    }
  }

  listen() {
    for (const el of document.querySelectorAll('a:not([href^="#"]):not([data-featherify="false"]):not([data-featherified]):not([download]):not([target="_blank"])')) {
      try {
        if (new URL(el.href).host !== window.location.host) {
          continue;
        }

        el.addEventListener('click', (e) => {
          if (e.defaultPrevented) {
            return;
          }

          this.go(el.href, true, el);

          e.preventDefault();
        });

        el.setAttribute('data-featherified', '');
      } catch (ignored) {
      }
    }

    return this;
  }

  async _next() {
    if (this.loading) {
      return;
    }

    const goItem = this.goQueue.shift();
    if (!goItem) {
      return;
    }

    try {
      this.eventData = {
        triggerElement: goItem.triggerElement,
        newDocument: null,
        target: goItem.location,
        recipientLocation: this.currentLocation
      };

      this.loading = true;

      this._triggerEvent('beforeUnmountAnimation');
      const currentContentContainers = document.documentElement.querySelectorAll(this.options.contentContainerSelector);
      for (const el of currentContentContainers) {
        el.classList.add('featherify-leaving');
        el.classList.remove('featherify-mounted');
        if (this.options.bodyTransitionClasses) {
          document.body.classList.add('featherify-leaving');
          document.body.classList.remove('featherify-entering');
        }
      }

      if (this.animationsAwaiting) {
        await new Promise((resolve) => {
          this.animatedHandler = () => {
            resolve();
          };
        });
      }

      this._triggerEvent('afterUnmountAnimation');

      this._triggerEvent('loadingData');
      const newHTML = new DOMParser().parseFromString(await fetch(goItem.location, {
        mode: 'no-cors'
      }).then((r) => {
        if (!r.headers.get('Content-Type').startsWith('text')) {
          return;
        }

        this._triggerEvent('progress', {
          status: 'html'
        });

        return r.text();
      }), 'text/html');

      this.eventData.newDocument = newHTML;
      if (goItem.pushState) {
        window.history.pushState(null, newHTML.title, goItem.location);
      }

      const currentStylesheets = [...document.head.querySelectorAll('link[rel="stylesheet"][href], style')];
      const currentStylesheetsIDs = currentStylesheets.map((el) => el.id);
      const newHTMLStylesheets = [...newHTML.querySelectorAll('link[rel="stylesheet"][href]')];
      const newHTMLStylesheetsIDs = newHTMLStylesheets.map((el) => el.id);

      const newStylesheets = newHTMLStylesheets.filter((el) => !currentStylesheetsIDs.includes(el.id));
      const redundantStylesheets = currentStylesheets.filter((el) => !newHTMLStylesheetsIDs.includes(el.id));

      let newStyles = [];
      for (const [i, el] of newStylesheets.entries()) {
        const style = document.createElement('style');
        style.id = el.id;
        style.innerHTML = await fetch(el.href, {
          mode: 'no-cors'
        }).then((r) => {
          this._triggerEvent('progress', {
            status: 'stylesheet',
            href: el.href,
            index: i,
            total: newStylesheets.length
          });

          return r.text();
        });

        newStyles.push(style);
      }

      const currentScripts = [...document.body.querySelectorAll('script')];
      const currentScriptsIDs = currentScripts.map((el) => el.src || el.getAttribute('data-src'));
      let newHTMLScripts = [...newHTML.querySelectorAll('script[src]')];
      const newHTMLScriptsIDs = newHTMLScripts.map((el) => el.src || el.getAttribute('data-src'));

      newHTMLScripts = newHTMLScripts.filter((el) => !currentScriptsIDs.includes(el.src || el.getAttribute('data-src')));
      const redundantScripts = currentScripts.filter((el) => !newHTMLScriptsIDs.includes(el.src || el.getAttribute('data-src')));

      const newScripts = [];
      for (const [i, newHTMLScript] of newHTMLScripts.entries()) {
        const script = document.createElement('script');
        const src = newHTMLScript.getAttribute('src');

        script.setAttribute('data-src', src);
        script.innerHTML = await fetch(src, {
          mode: 'no-cors'
        }).then((r) => {
          this._triggerEvent('progress', {
            status: 'script',
            src: src,
            index: i,
            total: newHTMLScripts.length
          });

          return r.text();
        });

        newScripts.push(script);
      }

      this._triggerEvent('loadedData');

      // Remove redundant styles + scripts.
      for (const el of redundantStylesheets) {
        el.parentNode.removeChild(el);
      }

      for (const el of redundantScripts) {
        try {
          const pathname = new URL(el.src || el.getAttribute('data-src')).pathname;
          this._triggerEvent('beforeUnmount ' + pathname.substr(pathname.lastIndexOf('/') + 1));
        } catch (ignored) {
        }

        if (document.body.contains(el)) {
          el.parentNode.removeChild(el);
        }
      }

      this._triggerEvent('beforeUnmount');
      document.title = newHTML.title;
      const newContentContainers = newHTML.querySelectorAll(this.options.contentContainerSelector);
      this._cloneAttributes(newHTML.body, document.body);
      this._cloneAttributes(newHTML.documentElement, document.documentElement);
      newContentContainers.forEach((el, i) => {
        currentContentContainers[i].innerHTML = el.innerHTML;
        el.classList.remove('featherify-leaving');
        el.classList.remove('featherify-mounted');
        if (this.options.bodyTransitionClasses) {
          document.body.classList.remove('featherify-mounted');
          document.body.classList.remove('featherify-leaving');
        }
        this._cloneAttributes(el, currentContentContainers[i]);
      });

      this._triggerEvent('unmounted');
      this._triggerEvent('mounted');

      // Add new styles + scripts.
      for (const style of newStyles) {
        document.head.appendChild(style);
      }

      for (const script of newScripts) {
        document.body.appendChild(script);
      }

      window.scrollTo(0, 0);
      this.listen();

      this._triggerEvent('beforeMountAnimation');

      if (this.animationsAwaiting) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            for (const el of currentContentContainers) {
              el.classList.add('featherify-entering');
              el.classList.add('featherify-mounted');
              if (this.options.bodyTransitionClasses) {
                document.body.classList.add('featherify-mounted');
                document.body.classList.add('featherify-entering');
              }
            }
          });
        });

        await new Promise((resolve) => {
          this.animatedHandler = () => {
            resolve();
          };
        });

        for (const el of currentContentContainers) {
          el.classList.remove('featherify-entering');
        }

        if (this.options.bodyTransitionClasses) {
          document.body.classList.remove('featherify-entering');
        }
      } else {
        for (const el of currentContentContainers) {
          el.classList.add('featherify-mounted');
        }

        if (this.options.bodyTransitionClasses) {
          document.body.classList.add('featherify-mounted');
        }
      }

      this._triggerEvent('afterMountAnimation');

      this.loading = false;
      this.currentLocation = new URL(goItem.location.href);
      goItem.resolve();
      this._next();
    } catch (e) {
      console.error(e);
      window.location = goItem.location;
    }
  }

  go(location, pushState = true, triggerElement) {
    if (typeof location === 'string') {
      location = new URL(location);
    } else {
      location = new URL(location.href);
    }

    if (this.currentLocation && location.pathname === this.currentLocation.pathname && location.search === this.currentLocation.search) {
      if (window.location.hash !== location.hash) {
        if (location.hash.length === 0) {
          history.pushState('', document.title, window.location.pathname + window.location.search);
        } else {
          window.location.hash = location.hash;
        }
      }

      return null;
    }

    return new Promise((resolve) => {
      this.goQueue.push({
        resolve,
        location,
        pushState,
        triggerElement
      });

      this._next();
    });
  }
}
